// ============================================================================
// AuthContext — identidade + permissões do usuário logado
// ----------------------------------------------------------------------------
// Três camadas de permissão convivem:
//
//  1. `permissions` (LEGADO) — mapa booleano por papel: { dev: true }.
//     Mantido porque as ~40 rotas antigas e dezenas de telas dependem dele.
//
//  2. `can(menuKey)` (NOVO) — permissão por ITEM DE MENU, editável no banco
//     pela tela /manutencao/papeis. É o que a sidebar e as rotas novas usam.
//
//  3. `podeAcao(key)` (NOVO) — permissão por AÇÃO dentro da tela (editar campo
//     travado, excluir, ver valores). Mesma tabela, key prefixada por `acao:`.
//     Catálogo em src/config/acoes.js.
//
// Origem das permissões novas, em ordem:
//   a) GET {urlSys}rbac/me/permissoes  → lista de menu_key liberadas
//   b) fallback: defaultPermissionsByRole() + defaultAcoesByRole()
// Assim o sistema nunca fica sem menu se a API cair.
// ============================================================================
import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { ROLE_BY_ID, ROLE_BY_KEY } from '../config/roles';
import { defaultPermissionsByRole, MENU_INDEX } from '../config/menu';
import { defaultAcoesByRole, acaoKey } from '../config/acoes';

const AuthContext = createContext();

// Lê o usuário do localStorage (gravado no login).
const readStoredUser = () => {
  try {
    const raw = localStorage.getItem('userData');
    if (raw) return JSON.parse(raw);
  } catch (e) {
    /* userData ausente ou corrompido: cai no fallback abaixo */
  }
  const nome = localStorage.getItem('userName');
  const id = localStorage.getItem('idUser');
  const tipo = localStorage.getItem('typeUser');
  if (!nome && !id && !tipo) return null;
  return { id_usuario: id, nome, id_tipo_usuario: tipo };
};

export const AuthProvider = ({ children }) => {
  const [permissions, setPermissions] = useState({});
  const [allowedKeys, setAllowedKeys] = useState(() => new Set());
  const [role, setRole] = useState(null);
  const [roleNome, setRoleNome] = useState(null);
  // Recorte de dados do papel (NÃO é permissão): setor usado no filtro da lista
  // de relatórios. Vem de tipos_usuario.setor_relatorio. 0 = sem recorte.
  const [setorRelatorio, setSetorRelatorio] = useState(0);
  const [user, setUser] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const carregar = useCallback(async () => {
    const userInfo = readStoredUser();
    setUser(userInfo);

    const typeUser = parseInt(
      (userInfo && userInfo.id_tipo_usuario) || localStorage.getItem('typeUser'),
      10
    );
    // Papel conhecido em código (papéis originais). Papel criado pela tela não
    // está aqui — a chave dele vem da API, logo abaixo.
    const roleKey = ROLE_BY_ID[typeUser] || null;
    setRole(roleKey);
    setRoleNome(ROLE_BY_KEY[roleKey]?.nome || null);
    setSetorRelatorio(ROLE_BY_KEY[roleKey]?.setor || 0);
    setPermissions(roleKey ? { [roleKey]: true } : {});

    // Permissões padrão do papel (fallback imediato, sem esperar a rede).
    // Junta os dois eixos: itens de menu + ações.
    const padrao = new Set([
      ...(defaultPermissionsByRole()[roleKey] || []),
      ...(defaultAcoesByRole()[roleKey] || []),
    ]);
    setAllowedKeys(padrao);

    // Sobrescreve com o que estiver configurado no banco, se a API responder.
    const urlSys = localStorage.getItem('urlSys');
    const token = localStorage.getItem('token');
    if (typeUser && urlSys && token) {
      try {
        const { data } = await axios.get(`${urlSys}rbac/me/permissoes`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 8000,
        });

        if (Array.isArray(data?.menu_keys)) {
          setAllowedKeys(new Set(data.menu_keys));
        }

        // `permissions` legado recebe DUAS chaves:
        //   - a própria chave do papel;
        //   - a chave_base (papel modelo), para o papel criado pela tela passar
        //     nas rotas antigas que checam uma lista fixa de papéis.
        // Sem isso, um papel novo veria o item no menu e levaria 'unauthorized'.
        if (data?.chave) {
          const mapa = { [data.chave]: true };
          if (data.chave_base) mapa[data.chave_base] = true;
          setPermissions(mapa);
          setRole(data.chave);
          setRoleNome(data.nome || ROLE_BY_KEY[data.chave]?.nome || null);
        }
        if (data?.setor_relatorio !== undefined) {
          setSetorRelatorio(Number(data.setor_relatorio) || 0);
        }
      } catch (err) {
        console.warn('[RBAC] usando permissões padrão do papel:', err?.message);
      }
    }

    setLoaded(true);
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  /** Pode ver/acessar este item de menu? `dev` enxerga tudo. */
  const can = useCallback(
    (menuKey) => {
      if (!menuKey) return true;
      if (role === 'dev') return true;
      return allowedKeys.has(menuKey);
    },
    [allowedKeys, role]
  );

  /**
   * Pode executar esta ação? Recebe a key CRUA do catálogo (sem o prefixo):
   *   podeAcao('saldos_patios.editar_aprovado')
   */
  const podeAcao = useCallback((key) => can(acaoKey(key)), [can]);

  /** Primeira rota liberada para o usuário (destino pós-login / fallback). */
  const primeiraRotaPermitida = useCallback(() => {
    const item = Object.values(MENU_INDEX).find((n) => n.path && !n.hidden && can(n.key));
    return item ? item.path : '/unauthorized';
  }, [can]);

  return (
    <AuthContext.Provider
      value={{
        permissions,
        loaded,
        user,
        role,
        roleNome,
        setorRelatorio,
        allowedKeys,
        can,
        podeAcao,
        primeiraRotaPermitida,
        recarregarPermissoes: carregar,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
